import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSave, FiArrowLeft, FiImage } from 'react-icons/fi';
import { API_BASE_URL } from '../config/config';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import Select from 'react-select';

const LegalPageForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(!!id);
  const [imagePreview, setImagePreview] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    page_type: '',
    url: '',
    description: '',
    meta_title: '',
    meta_keyword: '',
    meta_description: '',
    is_active: 1,
    feature_image: null
  });

  // Page Type Options
  const pageTypeOptions = useMemo(() => [
    { value: 'privacy_policy', label: 'Privacy Policy' },
    { value: 'terms_conditions', label: 'Terms & Conditions' },
    { value: 'refund_policy', label: 'Refund Policy' },
    { value: 'about_us', label: 'About Us' },
    { value: 'contact_us', label: 'Contact Us' },
    { value: 'other', label: 'Other' }
  ], []);

  // Custom Select Styles
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
      '&:hover': { borderColor: '#2563eb' },
      boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
      padding: '2px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#dbeafe' : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
      '&:active': { backgroundColor: '#2563eb' }
    })
  };

  useEffect(() => {
    if (id) {
      fetchPage();
    }
  }, [id]);

  const fetchPage = async () => {
    try {
      setFetchingData(true);
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.get(`${API_BASE_URL}/legal-page/${id}`, {
        headers: { 'X-User-ID': userId }
      });
      
      if (response.data.success) {
        const page = response.data.data;
        setFormData({
          title: page.title || '',
          page_type: page.page_type || '',
          url: page.url || '',
          description: page.description || '',
          meta_title: page.meta_title || '',
          meta_keyword: page.meta_keyword || '',
          meta_description: page.meta_description || '',
          is_active: page.is_active || 1,
          feature_image: null
        });
        if (page.feature_image) {
          setImagePreview(page.feature_image);
        }
      }
    } catch (error) {
      console.error('Error fetching page:', error);
      toast.error('Error loading page');
      navigate('/legal-pages');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    });
    
    // Auto-generate URL from title
    if (name === 'title' && !id) {
      const url = value.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData(prev => ({ ...prev, url }));
    }
  };

  const handlePageTypeChange = (selectedOption) => {
    setFormData({ ...formData, page_type: selectedOption ? selectedOption.value : '' });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, feature_image: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          submitData.append(key, formData[key]);
        }
      });

      const url = id 
        ? `${API_BASE_URL}/legal-page/${id}/update`
        : `${API_BASE_URL}/legal-page/create`;
      
      const response = await axios.post(url, submitData, {
        headers: {
          'X-User-ID': userId,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success(id ? 'Page updated successfully' : 'Page created successfully');
        navigate('/legal-pages');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving page');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const selectedPageType = pageTypeOptions.find(opt => opt.value === formData.page_type);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-8">
        <button
          onClick={() => navigate('/legal-pages')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft /> Back to Legal Pages
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {id ? 'Edit Legal Page' : 'Create New Legal Page'}
        </h1>
        <p className="text-gray-600">
          {id ? 'Update your legal page' : 'Create a new legal or informational page'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Featured Image */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Featured Image
          </label>
          <div className="flex items-start gap-4">
            {imagePreview && (
              <div className="w-48 h-32 border-2 border-gray-300 rounded-lg overflow-hidden">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="flex-1">
              <input 
                type="file" 
                accept="image/*"
                onChange={handleImageChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-sm text-gray-500 mt-1">
                Recommended: 1200x630px, JPG or PNG, max 5MB
              </p>
            </div>
          </div>
        </div>

        {/* Page Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Page Type *
          </label>
          <Select
            options={pageTypeOptions}
            value={selectedPageType}
            onChange={handlePageTypeChange}
            styles={customSelectStyles}
            placeholder="Select page type..."
            isClearable
            required
          />
        </div>

        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
            Title *
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            placeholder="Enter page title"
            required
          />
        </div>

        {/* URL Slug */}
        <div>
          <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-2">
            URL Slug *
          </label>
          <input
            type="text"
            id="url"
            name="url"
            value={formData.url}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            placeholder="page-url-slug"
            required
          />
          <p className="text-sm text-gray-500 mt-1">
            This will be used in the URL: /page/{formData.url}
          </p>
        </div>

        {/* Full Content */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Content *
          </label>
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            <CKEditor
              editor={ClassicEditor}
              data={formData.description}
              onChange={(event, editor) => {
                const data = editor.getData();
                setFormData({ ...formData, description: data });
              }}
              config={{
                toolbar: [
                  'heading', '|',
                  'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
                  'blockQuote', 'insertTable', '|',
                  'undo', 'redo'
                ]
              }}
            />
          </div>
        </div>

        {/* SEO Section */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">SEO Settings</h3>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="meta_title" className="block text-sm font-medium text-gray-700 mb-2">
                Meta Title
              </label>
              <input
                type="text"
                id="meta_title"
                name="meta_title"
                value={formData.meta_title}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                placeholder="SEO title (optional)"
              />
            </div>

            <div>
              <label htmlFor="meta_keyword" className="block text-sm font-medium text-gray-700 mb-2">
                Meta Keywords
              </label>
              <input
                type="text"
                id="meta_keyword"
                name="meta_keyword"
                value={formData.meta_keyword}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>

            <div>
              <label htmlFor="meta_description" className="block text-sm font-medium text-gray-700 mb-2">
                Meta Description
              </label>
              <textarea
                id="meta_description"
                name="meta_description"
                value={formData.meta_description}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                placeholder="SEO description (optional)"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={formData.is_active == 1}
            onChange={handleChange}
            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
          />
          <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
            Publish this page (uncheck to save as draft)
          </label>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate('/legal-pages')}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            <FiSave /> {loading ? 'Saving...' : (id ? 'Update Page' : 'Create Page')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LegalPageForm;
